import {AxiosConfig} from "./AxiosConfig/AxiosConfig";
import {IDraftMessageSummary} from "../Interfaces/IDraftMessageSummary";
import {IPagedResponse, ISentMessageSummary} from "../Interfaces/ISentMessageSummary";
import {IDraftMessage, IMessage, IRawMessage, ISentMessage} from "../Interfaces/Message";
import {IButtonMessage} from "../Interfaces/ButtonMessage";
import {ITranslationMessage} from "../Interfaces/TranslationMessage";
import {SentMessageSentDetails} from "../Interfaces/SentMessageSentDetails";

const parseRawMessage = (rawMessage: IRawMessage): IMessage => {
    const translations: Array<ITranslationMessage> = rawMessage.translations.map(t => {
        if (!t.buttons) t.buttons = new Array<IButtonMessage>();
        return t;
    });
    return {
        ...rawMessage,
        defaultLanguage: translations[0],
        translations: translations.slice(1, translations.length),
    };
}

const formatToRawMessage = (message: IMessage): IRawMessage => {
    return {
        id: message.id,
        translations: [message.defaultLanguage, ...message.translations],
        topicsId: message.topicsId,
        state: message.state,
        tenantId: message.tenantId,
        groupId: message.groupId,
        mailingListId: message.mailingListId,
        sendType: message.sendType,
        channel: message.channel,
        gptId: message.gptId,
        authorName: message.authorName
    };
}

export const getSentNotificationsSummaries = async (
    page: number = 1,
    pageSize: number = 10
): Promise<IPagedResponse<ISentMessageSummary>> => {
    return new Promise<IPagedResponse<ISentMessageSummary>>((resolve) => {
        return AxiosConfig.instance.get(`/sentNotifications?page=${page}&pageSize=${pageSize}`).then(res => {
            return resolve(res.data);
        }).catch(error => {
            console.error(error);
            resolve({ items: [], totalCount: 0, page, pageSize });
        });
    });
}

export const getDraftNotificationsSummaries = async (): Promise<Array<IDraftMessageSummary>> => {
    return new Promise<Array<IDraftMessageSummary>>((resolve, reject) => {
        return AxiosConfig.instance.get(`/draftNotifications`).then(res => {
            return resolve(res.data);
        }).catch(error => {
            reject(error);
        });
    });
}

export const getSentNotification = async (id: string): Promise<ISentMessage> => {
    return new Promise<ISentMessage>((resolve, reject) => {
        return AxiosConfig.instance.get("/sentNotifications/" + id).then(res => {
            return resolve(parseRawMessage(res.data) as ISentMessage);
        }).catch(error => {
            reject(error);
        });
    });
}

export const getDraftNotification = async (id: string): Promise<IDraftMessage> => {
    return new Promise<IDraftMessage>((resolve, reject) => {
        return AxiosConfig.instance.get(`/draftNotifications/${id}`).then(res => {
            return resolve(parseRawMessage(res.data));
        }).catch(error => {
            reject(error);
        });
    });
}

export const getNotificationFeedbacks = async (id: string): Promise<any> => {
    return new Promise<any>((resolve, reject) => {
        return AxiosConfig.instance.get(`/feedbacks/${id}`).then(res => {
            return resolve(res.data);
        }).catch(error => {
            reject(error);
        });
    });
}

export const deleteDraftNotification = async (id: string): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
        return AxiosConfig.instance.delete(`/draftNotifications/${id}`).then(() => {
            return resolve();
        }).catch(error => {
            reject(error);
        });
    });
}

export const duplicateDraftNotification = async (id: string): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
        return AxiosConfig.instance.post(`/draftNotifications/${id}/duplicates`).then(res => {
            return resolve(res.data);
        }).catch(error => {
            reject(error);
        });
    });
}

export const sendDraftNotification = async (id: string, delayedDate: string | null): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
        const searchParams = delayedDate ? `?delayedDate=${delayedDate}` : "";
        return AxiosConfig.instance.post("/sentNotifications" + searchParams, id).then(res => {
            return resolve(res.data);
        }).catch(error => {
            reject(error);
        });
    });
}

export const sendToMySelfDraftNotification = async (id: string): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
        return AxiosConfig.instance.get(`/sentNotifications/${id}/tomyself`).then(res => {
            return resolve(res.data);
        }).catch(error => {
            reject(error);
        });
    });
}

export const updateDraftNotification = async (data: IDraftMessage): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
        return AxiosConfig.instance.put("/draftNotifications", formatToRawMessage(data)).then(() => {
            return resolve();
        }).catch(error => {
            reject(error);
        });
    });
}

export const createDraftNotification = async (data: IDraftMessage): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
        return AxiosConfig.instance.post("/draftNotifications", formatToRawMessage(data)).then(res => {
            return resolve(res.data);
        }).catch(error => {
            reject(error);
        });
    });
}

export const getSentNotificationSentDetails = async (id: string): Promise<SentMessageSentDetails> => {
    return new Promise<SentMessageSentDetails>((resolve, reject) => {
        return AxiosConfig.instance.get(`/sentNotifications/${id}/sent-details`).then(res => {
            return resolve(res.data);
        }).catch(error => {
            reject(error);
        });
    });
}

export const exportSendDraftNotificationSentDetails = async (id: string): Promise<Blob> => {
    return new Promise<Blob>((resolve, reject) => {
        AxiosConfig.instance.get(`/sentNotifications/${id}/sent-details/export`).then(response => {
            const contentType = response.headers["content-type"] ?? "text/plain";
            const base64File = `data:${contentType};base64,${response.data}`;
            fetch(base64File).then(res => res.blob()).then(blob => resolve(blob));
        }).catch(error => {
            reject(error);
        });
    });
}

export const resendNotificationToUnread = async (id: string): Promise<boolean> => {
    try {
        await AxiosConfig.instance.get(`/sentNotifications/${id}/resend-unread`);
        return true;
    } catch (error) {
        console.error(error);
        return false;
    }
}